import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { fullDate } from '@app/utils/helpers/date-utils';
import { ProductStatus, SprintEntity } from '@data';
import { fetchProduct, fetchProducts, fetchProductStore, writeProductStore } from '@data/api/products';
import { useListProducts } from '@data/hooks/products';
import { useListSprints } from '@data/hooks/sprints';

import {
  keepProfileValues,
  LevelChange,
  ProfileValues,
  setProfileFrom,
} from '../../products/productDetail/levelFromSprint';
import { loadProductStore, productStoreMark, settleProductStore } from '../../products/productDetail/productStore';
import { formatDay, startOfYear, yearOf } from '../../products/productDetail/productVersions';
import { todayKey } from '../../products/productDetail/useProductVersions';

/** The sprint running today: the latest one that has started, read as days the way Insights reads them. */
export const runningSprint = (sprints: SprintEntity[], today: string): SprintEntity | undefined =>
  sprints
    .filter(sprint => fullDate(sprint.startDate) <= today)
    .sort((a, b) => fullDate(b.startDate).localeCompare(fullDate(a.startDate)))[0];

/** The products somebody is on, and giving them a new level or chapter on the ones picked from the running sprint. */
export const useProductLevelChange = (organizationId: string | undefined, membershipId: string, isAsking: boolean) => {
  const { t } = useTranslation();
  const today = todayKey();
  // Only asked for while the dialog is open: most saves change nothing about a level.
  const { data: productsData, isLoading } = useListProducts(isAsking ? organizationId : undefined, {
    teamMembershipId: membershipId,
    statuses: [ProductStatus.active, ProductStatus.support],
  });
  const products = useMemo(
    () => (productsData?.items ?? []).map(product => ({ id: product.id, name: product.name })),
    [productsData],
  );
  // The same query the hours card below asks, so it is usually answered already.
  const { data: sprintsData } = useListSprints({ limit: 200 });
  const sprint = runningSprint(sprintsData?.items ?? [], today);
  // A sprint begun last year cannot split this year's versions, so the level starts on 1 January instead.
  const yearStart = startOfYear(yearOf(today));
  const sprintStart = sprint ? fullDate(sprint.startDate) : today;
  const from = sprintStart < yearStart ? yearStart : sprintStart;
  const fromLabel =
    sprint && from === sprintStart
      ? t('memberDetails.productLevels.fromSprint', { number: sprint.number })
      : formatDay(from);
  const [isApplying, setIsApplying] = useState(false);

  const names = useRef(new Map<string, string>());
  const nameOf = (productId: string): string =>
    products.find(product => product.id === productId)?.name ?? names.current.get(productId) ?? productId;
  // A product that could not keep its values follows the profile, so it is named with the ones that failed.
  const keepFailed = useRef<string[]>([]);

  /** Before the profile changes: every product they are on keeps what it shows now, in each version with no value of its own. */
  const keep = async (previous: { level?: string; chapter: boolean }): Promise<void> => {
    if (!organizationId) return;
    setIsApplying(true);
    // Read here, closed ones too: a clear saves with no dialog, and no closed product may follow the profile either.
    const every = await fetchProducts(organizationId, { teamMembershipId: membershipId }).catch((error: unknown) => {
      setIsApplying(false);
      throw error;
    });
    every.items.forEach(product => names.current.set(product.id, product.name));
    const everyId = every.items.map(product => product.id);
    const settled = await Promise.allSettled(
      everyId.map(async productId => {
        // Loaded first: the store drops a write to a product it never loaded.
        await loadProductStore(productId, { fetch: fetchProductStore, write: writeProductStore });
        // The product's own reading: with no default chapter that is the one they first reported under there.
        const shown = previous.chapter
          ? (await fetchProduct(productId)).team?.find(member => member.membershipId === membershipId)?.chapterKey
          : undefined;
        const mark = productStoreMark(productId);
        keepProfileValues(productId, membershipId, { level: previous.level, chapter: shown || undefined });
        if (!(await settleProductStore(productId, mark)))
          throw new Error(`The API refused the kept values on ${productId}`);
      }),
    );
    setIsApplying(false);
    keepFailed.current = everyId.filter((_, index) => settled[index].status === 'rejected').map(nameOf);
  };

  /** After the profile is saved: the picked products get `next` from the running sprint. */
  const apply = async (productIds: string[], next: ProfileValues): Promise<void> => {
    const failedToKeep = keepFailed.current;
    keepFailed.current = [];
    setIsApplying(true);
    // Settled one by one: a product that fails to load must not hide the ones that did change.
    const settled = await Promise.allSettled(
      productIds.map(async (productId): Promise<LevelChange> => {
        await loadProductStore(productId, { fetch: fetchProductStore, write: writeProductStore });
        const mark = productStoreMark(productId);
        const outcome = setProfileFrom(productId, membershipId, next, from, today);
        // Counted only once the API has kept it: the store sends a tick later and swallows a refusal.
        if (!(await settleProductStore(productId, mark)))
          throw new Error(`The API refused the new values on ${productId}`);
        return outcome;
      }),
    );
    setIsApplying(false);

    const named = (keepIt: (outcome: PromiseSettledResult<LevelChange>) => boolean): string[] =>
      productIds.filter((_, index) => keepIt(settled[index])).map(nameOf);
    const changed = named(outcome => outcome.status === 'fulfilled' && outcome.value !== 'skipped');
    const skipped = named(outcome => outcome.status === 'fulfilled' && outcome.value === 'skipped');
    const failed = [...new Set([...failedToKeep, ...named(outcome => outcome.status === 'rejected')])];
    if (changed.length)
      toast.success(t('memberDetails.productLevels.applied', { count: changed.length, from: fromLabel }));
    if (skipped.length) toast.warning(t('memberDetails.productLevels.skipped', { products: skipped.join(', ') }));
    if (failed.length) toast.error(t('memberDetails.productLevels.failed', { products: failed.join(', ') }));
  };

  return { products, isLoading, fromLabel, isApplying, keep, apply };
};
